import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges,} from '@angular/core';
import {TranslocoService} from '@ngneat/transloco';
import {
    AmountStatus,
    ApplicationFormSettings,
    CategoryItem,
    Competitor,
    CompetitorFilter,
    CompetitorStatus,
    CompetitorViewContext,
    CompetitorViewData,
    CompetitorViewDataAvailableFields, CompetitorViewHelper,
    Country,
    EventItem,
    Lookups,
    PagedFilter,
    Pages,
    Race,
    RaceAdditionalPricingItem,
    RaceCommercialItem,
    RaceSettings,
    TeamType,
} from "@model";
import {CompetitorService, CompetitorTableCacheService, RaceCommercialService} from "@services";
import {ActivatedRoute} from '@angular/router';
import {
  OnEditCompetitor
} from "../competitor-table-dropdown-actions-row/competitor-table-dropdown-actions-row.component";
import {Observable} from "rxjs";
import {PagingComponent} from "../paging.component";
import {FilterGroup, FilterGroupType, FilterItem} from '@components';
import {getLanguageValue} from "@utility";

@Component({
    selector: 'app-competitor-table',
    templateUrl: './competitor-table.component.html',
    styleUrls: ['./competitor-table.component.scss'],
    standalone: false
})
export class CompetitorTableComponent extends PagingComponent<Competitor> implements OnInit, OnChanges {
    public static readonly MIN_SEARCH_INPUT_LENGTH: number = 3;
    public static readonly MIN_SEARCH_INPUT_LENGTH_ADMIN: number = 1;

    @Input() competitors: Competitor[] = [];
    @Input() race: Race | undefined;
    @Input() event: EventItem | undefined;
    @Input() isAdmin: boolean = false;
    @Input() isTeamMembersManagement: boolean = false;
    @Input() showBorderBottom: boolean = true;
    @Input() categories: CategoryItem[] = [];
    @Input({required: true}) languages: Lookups[] = [];
    @Input({required: true}) countries: Country[] = [];
    @Input({required: true}) genders: Lookups[] = [];
    @Input() filteringEnabled: boolean = true;
    @Input() raceFilteringEnabled: boolean = false;
    @Input() showEmptyState: boolean = true;
    @Input() showHeader: boolean = false;
    @Input() raceSettingsList: RaceSettings[] = [];
    @Input() applicationFormSettingsList: ApplicationFormSettings[] = []
    @Input() raceAdditionalPricingItems: RaceAdditionalPricingItem [] = [];
    @Input() raceCommercialItems: RaceCommercialItem[] = [];
    @Input() measurements: boolean = false;
    @Input() allowOnlyRaceView: boolean = false;
    @Output() filterChange: EventEmitter<PagedFilter<CompetitorFilter>> = new EventEmitter<PagedFilter<CompetitorFilter>>();
    @Output() competitorListChanged: EventEmitter<Competitor[]> = new EventEmitter<Competitor[]>();
    @Output() onEditTeamMemberButtonClick: EventEmitter<OnEditCompetitor> = new EventEmitter<OnEditCompetitor>();
    @Output() chronometarRaceCompetitorStartStop = new EventEmitter<Race>();
    @Output() refreshCounters = new EventEmitter<void>();

    competitorFiler: CompetitorFilter = new CompetitorFilter();

    competitorStatusByNumber: Map<number, CompetitorStatus> = new Map(
        [
            CompetitorStatus.REG, CompetitorStatus.BIB, CompetitorStatus.RDY,
            CompetitorStatus.STA, CompetitorStatus.FIN, CompetitorStatus.DSQ,
            CompetitorStatus.DNF, CompetitorStatus.TLE, CompetitorStatus.DNS
        ].map((status, index) => [index, status])
    );

    viewDataHelper: CompetitorViewHelper | undefined;
    commercialItemsMap: Map<number, RaceCommercialItem[]> = new Map<number, RaceCommercialItem[]>();
    additionalPricingItemsMap: Map<number, RaceAdditionalPricingItem[]> = new Map<number, RaceAdditionalPricingItem[]>();
    raceSettingsMap: Map<number, RaceSettings> = new Map<number, RaceSettings>();
    applicationFormSettingsMap: Map<number, ApplicationFormSettings> = new Map<number, ApplicationFormSettings>();
    raceMap: Map<number, Race> = new Map<number, Race>();
    currentViewData: CompetitorViewDataAvailableFields | undefined;

    competitorViewData: CompetitorViewData[] = [];

    filterGroupsMap: Map<number, Map<string, FilterGroup>> = new Map<number, Map<string, FilterGroup>>();
    activeRaceFilterGroups: Map<string, FilterGroup> | undefined;
    activeFilterGroups: Map<string, FilterGroup> | undefined;

    protected readonly AmountStatus = AmountStatus;
    protected readonly TeamType = TeamType;
    protected readonly MIN_SEARCH_INPUT_LENGTH = CompetitorTableComponent.MIN_SEARCH_INPUT_LENGTH;
    protected readonly MIN_SEARCH_INPUT_LENGTH_ADMIN = CompetitorTableComponent.MIN_SEARCH_INPUT_LENGTH_ADMIN;
    protected readonly FilterGroupType = FilterGroupType;


    constructor(private translocoService: TranslocoService,
                private competitorService: CompetitorService,
                private raceCommercialService: RaceCommercialService,
                private route: ActivatedRoute, private competitorTableCache: CompetitorTableCacheService) {
        super();
    }

    override ngOnInit(): void {
        super.ngOnInit();
        this.init();
        this.handleExploreResult(this.competitors);
    }


    ngOnChanges(changes: SimpleChanges): void {
        if (changes['competitors'] && !changes['competitors'].firstChange) {
            if (!this.allowOnlyRaceView) {
                this.race = undefined;
            }
            this.buildAllFilterItems();
            this.handleRaceSelection(this.allowOnlyRaceView ? this.race?.id : undefined, true);
            this.competitorFiler = new CompetitorFilter(this.event?.id);
            this.page = 1;
            this.handleExploreResult(this.competitors);
        }
    }


    private init() {
        this.initRaceMap();
        this.buildMaps();

        if (this.isAdmin) {
            if (!this.measurements) {
                this.competitorStatusByNumber = new Map<number, CompetitorStatus>(
                    [CompetitorStatus.RFP, ...Array.from(this.competitorStatusByNumber.values())]
                        .map((status, index) => [index, status])
                );
            }
        }
        if (this.race) {
            this.handleRaceSelection(this.race.id, true)
        }
        this.initView();
        this.buildAllFilterItems();
        this.setView();
        this.setViewData();
    }

    private buildMaps() {
        if (this.raceSettingsList?.length) {
            this.raceSettingsList?.forEach(settings => {
                if (settings.raceId) {
                    this.raceSettingsMap.set(settings.raceId, settings)
                }
            })
        }
        this.applicationFormSettingsList?.forEach(settings => {
            if (settings.raceId) {
                this.applicationFormSettingsMap.set(settings.raceId, settings)
            }
        })
        this.raceAdditionalPricingItems?.forEach((p) => {
            const raceId = p.raceId;
            if (!raceId) return;

            const list = this.additionalPricingItemsMap.get(raceId) ?? [];
            list.push(p);
            this.additionalPricingItemsMap.set(raceId, list);
        });

        this.raceCommercialItems?.forEach((p) => {
            const raceId = p.raceId;
            if (!raceId) return;

            const list = this.commercialItemsMap.get(raceId) ?? [];
            list.push(p);
            this.commercialItemsMap.set(raceId, list);
        });
    }

    private initTableColumnsFromCache() {
        Array.from(this.raceMap.keys()).forEach(raceId => {
            this.competitorTableCache.getCompetitorTableColsForEvent(this.event!.id!, raceId, this.measurements).subscribe(selected => {
                if (selected?.selectedCols?.length) {
                    this.viewDataHelper?.viewDataByRaceIds.get(raceId)?.options?.forEach(option => {
                        option.selected = selected.selectedCols.includes(option.key)
                    });
                }
            })
        });

        this.competitorTableCache.getCompetitorTableColsForEvent(this.event!.id!, undefined, this.measurements).subscribe(selected => {
            if (selected?.selectedCols?.length) {
                this.viewDataHelper?.eventViewData?.options?.forEach(option => {
                    option.selected = selected.selectedCols.includes(option.key)
                });
            }
        });
    }


    initView() {
        this.viewDataHelper = new CompetitorViewHelper(
            this.event!.id!, this.translocoService,
            this.countries, this.applicationFormSettingsMap,
            this.event?.paymentSettings, this.raceSettingsMap,
            this.commercialItemsMap, this.additionalPricingItemsMap,
            this.isAdmin ? CompetitorViewContext.ADMINISTRATION : CompetitorViewContext.PUBLIC,
            this.measurements, this.raceMap,
            this.isTeamMembersManagement ? this.race : undefined)

        if (this.isAdmin && !this.measurements && !this.isTeamMembersManagement) {
            this.initTableColumnsFromCache()
        }
    }


    sortCompetitors() {
        const byId = new Map<number, Competitor>();
        for (const c of this.competitors) {
            if (c.id != null) byId.set(c.id, c);
        }
        this.competitors = this.competitorViewData
            .map((row) => (row.id != null ? byId.get(row.id) : undefined))
            .filter((c): c is Competitor => !!c);
    }

    onEditCompetitor(onEditCompetitor: OnEditCompetitor) {
        const {competitor, index} = onEditCompetitor;
        this.competitors[index] = competitor;
        if (this.competitorViewData.at(index) && this.viewDataHelper) {
            this.competitorViewData[index] = this.viewDataHelper.competitorToCompetitorViewData(competitor);
        }
        this.refreshCounters.emit();
    }

    keepOriginalOrder = () => 0;


    initRaceMap() {
        if (!this.isTeamMembersManagement && !this.allowOnlyRaceView) {
            this.event?.racesList?.forEach((race: Race) => {
                this.raceMap.set(race.id!, race);
            });
        }
        else if (this.race?.id) {
            this.raceMap.set(this.race.id, this.race)
        }
        if (this.raceMap.size === 1) {
            this.allowOnlyRaceView = true;
            if (!this.race) {
                this.race = Array.from(this.raceMap.values())[0]!;
            }
        }
    }

    onSelectedFieldsChange(selectedFields: (string | number)[]) {
        if (this.currentViewData) {
            this.viewDataHelper?.selectFields(selectedFields, this.currentViewData)
            if (!selectedFields.length) {
                this.currentViewData.options = new Map(this.currentViewData.options);
            }
        }
    }


    handleRaceSelection(value?: any, preventLoading: boolean = false): void {
        this.competitorFiler = new CompetitorFilter()
        if ((value === this.race?.id || !value)) {
            if (!this.allowOnlyRaceView) {
                this.race = this.raceMap.get(value);
            } else {
                preventLoading = true;
            }
        } else {
            this.race = this.raceMap.get(value);
        }
        this.competitorFiler.raceId = this.race?.id;
        this.setView()
        if (!preventLoading) {
            this.onExplore(false);
        }
    }

    setView() {
        if (this.race?.id) {
            if (this.isTeamMembersManagement) {
                this.currentViewData = this.viewDataHelper?.viewDataByRaceIds?.get(this.race.id)?.teamMemberView;
            }
            else {
                this.currentViewData = this.viewDataHelper?.viewDataByRaceIds.get(this.race.id);
                this.activeRaceFilterGroups?.forEach(g => {
                    if (g.key !== 'race') {
                        g.items.forEach(i => i.selected = false)
                    }
                })
                this.activeRaceFilterGroups = this.filterGroupsMap.get(this.race.id);
            }
        }
        else {
            this.currentViewData = this.viewDataHelper?.eventViewData;
            this.activeRaceFilterGroups?.forEach(g => {
                    g.items.forEach(i => i.selected = false)
            })
            this.activeRaceFilterGroups = undefined;
        }
    }

    protected fetchPage(page: number): Observable<Competitor[]> {
        return this.competitorService.findCompetitors(
            new PagedFilter(new Pages(undefined, page), this.getFilter()), this.isAdmin, this.measurements);
    }

    protected override afterSetItems(items: Competitor[]): void {
        if (items && items.length > 0) {
            this.competitors = items;
        } else {
            this.competitors = [];
        }
        this.setViewData();
    }

    setViewData() {
        if (this.viewDataHelper && this.competitors.length) {
            this.competitorViewData = this.competitors.map(c => this.viewDataHelper!.competitorToCompetitorViewData(c, !this.isTeamMembersManagement));
        }
        else {
            this.competitorViewData = []
        }
    }

    protected override afterOnExplore(): void {
        this.scrollToTop();
    }


    editTeamMemberButtonClick(value: OnEditCompetitor) {
        this.onEditTeamMemberButtonClick.emit(value);
    }

    onChronometarRaceCompetitorStartStop(race: Race) {
        this.chronometarRaceCompetitorStartStop.emit(race);
        this.refreshCounters.emit();
    }

    saveToCache() {
        if (this.viewDataHelper && this.isAdmin && !this.isTeamMembersManagement) {
            const selectedKeys = [...(this.viewDataHelper.eventViewData?.options?.values() ?? [])]
                .filter(o => o.selected).map(o => o.key);
            if (selectedKeys.length) {
                this.competitorTableCache.saveCompetitorTableColsForEvent(this.event!.id!, undefined, selectedKeys);
            }
            Array.from(this.raceMap.keys()).forEach(raceId => {
                const raceSelected = [...(this.viewDataHelper?.viewDataByRaceIds.get(raceId)?.options?.values() ?? [])]
                    .filter(o => o.selected).map(o => o.key)
                if (raceSelected.length) {
                    this.competitorTableCache.saveCompetitorTableColsForEvent(this.event!.id!, raceId, raceSelected);
                }
            })
        }
    }


    assignClass(field: string): string {
        if (field === 'id') {
            return 'id-width';
        } else if (field === 'startBlock') {
            return 'start-block-width';
        } else if (field === 'startNumber') {
            return 'start-number-width';
        } else if (field === 'registrationId') {
            return 'id-width';
        } else if (field === 'email') {
            return 'email-width';
        } else if (field === 'created') {
            return 'created-width';
        } else if (field === 'modified') {
            return 'modified-width';
        } else if (field === 'birthday') {
            return 'birthday-width';
        } else if (field === 'fullName' || field === 'teamName') {
            return 'full-name-width';
        } else if (field === 'raceName') {
            return 'race-name-width';
        } else if (field === 'startTime') {
            return 'start-time-width';
        } else if (field === 'paymentStatus') {
            return 'payment-status-width'
        } else if (field === 'sex') {
            return 'gender-col-width'
        } else if (field === 'status') {
            return 'th-status';
        }
        return '';
    }

    private getFilter(): CompetitorFilter {
        const filter: CompetitorFilter = {...this.competitorFiler};
        filter.eventId = this.event?.id ?? (+this.route.snapshot.queryParams['eventId'] || undefined);
        this.filterChange.emit(new PagedFilter(new Pages(undefined, this.page), filter));
        return filter;
    }


    onDeleteCompetitor() {
        this.onExplore(false);
        this.refreshCounters.emit();
    }


    private buildRaceFilterGroup(): FilterGroup | undefined {
        if (this.raceFilteringEnabled && ((this.event?.racesList?.length ?? 0) > 0)) {
            const raceItems = (this.event?.racesList ?? []).map((race: Race) =>
                new FilterItem(race.id, undefined,
                    getLanguageValue(race.name,
                        race.nameEng, race.nameIta,
                        race.nameSlo, race.nameSr,
                        race.nameBs, this.translocoService.getActiveLang()) ?? "")
            );
            if (raceItems.length > 0) {
                return new FilterGroup(
                    'race', 'race', 'app.common.race',
                    FilterGroupType.SINGLE_SELECT, false,
                    raceItems, false, true
                )
            }
            return undefined;
        }
        return undefined;
    }

    private buildGenderFilterGroup(): FilterGroup | undefined  {
        if (this.genders.length) {
            return new FilterGroup('gender', 'gender', 'app.common.gender',
                FilterGroupType.SINGLE_SELECT, false, [
                    ...(this.genders ?? []).map((item: Lookups) =>
                        new FilterItem(item.id, undefined, "app.common.gender." + item.id, false)
                    )
                ], false, true)
        }
        else return;
    }

    private toCategoryFilterGroup(categories: CategoryItem[], gender?: number, selectedCategoryId?: number): FilterGroup | undefined {
        const filteredCategories = gender !== undefined
            ? categories.filter((item: CategoryItem) => item.gender === gender)
            : categories;

        if (filteredCategories.length === 0) return undefined;

        const categoryItems = filteredCategories.map((item: CategoryItem) =>
            new FilterItem(item.id, item.code, undefined, item.id === selectedCategoryId)
        );

        return new FilterGroup('category',
            'category', 'app.common.category',
            FilterGroupType.SINGLE_SELECT, categoryItems.length > 10,
            categoryItems, false, true)
    }

    private buildStatusFilterGroup(): FilterGroup | undefined {
        if (!this.isAdmin) return undefined;
        const items = [
            ...Array.from(this.competitorStatusByNumber.entries()).map(([key, status]: [number, CompetitorStatus]) =>
                new FilterItem(key, undefined, 'competitor.status.' + status.toString(), false)
            )
        ];
        return new FilterGroup(
            'status', 'status', 'competitor.table.status',
            FilterGroupType.SINGLE_SELECT,
            false, items, false, true
        )
    }

    private buildAllFilterItems(): void {
        const raceFilterGroup = this.buildRaceFilterGroup();
        const statusFilterGroup = this.buildStatusFilterGroup();
        this.viewDataHelper?.viewDataByRaceIds.forEach(
            (value: CompetitorViewDataAvailableFields, key: number) => {
                const filterGroups: Map<string, FilterGroup> = new Map<string, FilterGroup>();
                const raceSettings: RaceSettings | undefined = this.raceSettingsMap.get(key);
                if (raceFilterGroup && !this.allowOnlyRaceView) {
                    filterGroups.set('race', raceFilterGroup);
                }
                if (statusFilterGroup) {
                    filterGroups.set('status', statusFilterGroup);
                }
                if (value.options.has('sex')) {
                    const genderFilterGroup = this.buildGenderFilterGroup();
                    if (genderFilterGroup) {
                        filterGroups.set('gender', genderFilterGroup);
                    }
                }
                if (value.options.has('categoryLabel') && raceSettings?.category?.items?.length) {
                    const categoryGroup = this.toCategoryFilterGroup(raceSettings!.category!.items!);
                    if (categoryGroup) {
                        filterGroups.set('category', categoryGroup);
                    }
                }
                this.filterGroupsMap.set(key, filterGroups);
            });
        if (!this.allowOnlyRaceView && raceFilterGroup) {
            const eventFilters: Map<string, FilterGroup> = new Map<string, FilterGroup>();
            eventFilters.set('race', raceFilterGroup);
            if (statusFilterGroup) {
                eventFilters.set('status', statusFilterGroup);
            }
            this.activeFilterGroups = eventFilters;
        }
    }


    onFilterChange(result: Record<string, any>): void {
        const {searchTerm, race: raceId, gender, category, status: statusId} = result;
        const normalizedRaceId = raceId ?? undefined;
        const currentRaceId = this.race?.id ?? undefined;
        if (!this.allowOnlyRaceView && this.raceFilteringEnabled && normalizedRaceId !== currentRaceId) {
            this.handleRaceSelection(normalizedRaceId);
        }
        else {
            const normalizedGender = gender ?? undefined;
            const normalizedCurrentGender = this.competitorFiler.gender ?? undefined;

            if (currentRaceId != null && normalizedGender !== normalizedCurrentGender) {
                const raceSettings = this.raceSettingsMap.get(currentRaceId);
                const previousCategoryId = this.competitorFiler.categories?.[0];

                this.competitorFiler.gender = normalizedGender;

                const categoryGroup = this.toCategoryFilterGroup(
                    raceSettings?.category?.items ?? [], normalizedGender, previousCategoryId
                );

                const currentGroups = this.filterGroupsMap.get(currentRaceId);
                if (currentGroups) {
                    if (categoryGroup) {
                        currentGroups.set('category', categoryGroup);
                        const stillSelected = categoryGroup.items.find(i => i.selected);
                        this.competitorFiler.categories = (stillSelected && stillSelected.id != null)
                            ? [stillSelected.id] : undefined;
                    } else {
                        currentGroups.delete('category');
                        this.competitorFiler.categories = undefined;
                    }
                    this.activeRaceFilterGroups = new Map(currentGroups);
                }
            }
            else {
                this.competitorFiler.categories = category != null ? [category] : undefined;
                this.competitorFiler.gender = normalizedGender;
            }

            this.competitorFiler.name = searchTerm;
            if (this.isAdmin) {
                const competitorStatus = statusId != null ? this.competitorStatusByNumber.get(statusId) : undefined;
                this.competitorFiler.competitorStatuses = competitorStatus ? [competitorStatus] : undefined;
            }
            this.onExplore(false);
        }
    }


    override ngOnDestroy(): void {
        this.saveToCache();
        super.ngOnDestroy();
    }
}


export enum CompetitorTableAction {
    START = 1,
    CANCEL_START = 2,
    SET_COMPETITOR_DNS = 3,
    SET_COMPETITOR_BIB = 4,
    SET_COMPETITOR_RDY = 5,
    EDIT_COMPETITOR_TIMES = 6,
    EDIT_COMPETITOR = 7,
    DELETE_COMPETITOR = 8,
    ASSIGN_START_NUMBER = 9,
    CHANGE_COMPETITOR_RACE = 10,
    CONFIRM_REGISTRATION = 11,
    DELETE_COMPETITOR_TIMES = 12,
    EDIT_TEAM_MEMBER = 13,
}